import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ArrowLeft, Building2, Zap, TrendingUp, Activity, ArrowRight } from 'lucide-react';
import { districts, sites, alerts, complaints } from '../mock';

const DistrictView = () => {
  const { districtId } = useParams();
  const navigate = useNavigate();
  const district = districts.find(d => d.id === districtId);
  const districtSites = sites.filter(s => s.district === districtId);
  const districtAlerts = alerts.filter(a => a.district === districtId);
  const districtComplaints = complaints.filter(c => c.district === districtId);

  if (!district) {
    return <div>District not found</div>;
  }

  const getStatusColor = (status) => {
    if (status === 'active') return '#10ac84';
    if (status === 'moderate') return '#f39c12';
    return '#e74c3c';
  };

  const getDeviceStatusColor = (status) => {
    if (status === 'online') return '#10ac84';
    if (status === 'warning') return '#f39c12';
    return '#e74c3c';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/state-view')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to State View
          </Button>
          <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>{district.name} District</h1>
          <p className="text-base mt-2" style={{ color: '#576574' }}>District-level Performance and Site Management</p>
        </div>
      </div>

      {/* District KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                <Building2 className="w-6 h-6" style={{ color: '#10ac84' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{district.sites}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Total Sites</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                <Zap className="w-6 h-6" style={{ color: '#10ac84' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{district.capacity} MW</div>
            <div className="text-sm" style={{ color: '#576574' }}>Installed Capacity</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e3f2fd' }}>
                <TrendingUp className="w-6 h-6" style={{ color: '#3498db' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{district.todayGen} kWh</div>
            <div className="text-sm" style={{ color: '#576574' }}>Today's Generation</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fff4e6' }}>
                <Activity className="w-6 h-6" style={{ color: '#f39c12' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{district.pr}%</div>
            <div className="text-sm" style={{ color: '#576574' }}>Performance Ratio</div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Performance Ratio (PR)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold" style={{ color: '#f39c12' }}>{district.pr}%</span>
              <Badge variant="outline" style={{ backgroundColor: district.pr >= 70 ? '#e8f5f1' : '#fff4e6', color: district.pr >= 70 ? '#10ac84' : '#f39c12' }}>
                {district.pr >= 70 ? 'Good' : 'Needs Improvement'}
              </Badge>
            </div>
            <Progress value={district.pr} className="h-3" />
            <div className="mt-4 text-sm" style={{ color: '#576574' }}>Target: 75%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Capacity Utilization Factor (CUF)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold" style={{ color: '#9b59b6' }}>{district.cuf}%</span>
              <Badge variant="outline" style={{ backgroundColor: district.cuf >= 17 ? '#e8f5f1' : '#fff4e6', color: district.cuf >= 17 ? '#10ac84' : '#f39c12' }}>
                {district.cuf >= 17 ? 'Good' : 'Below Target'}
              </Badge>
            </div>
            <Progress value={district.cuf * 5} className="h-3" />
            <div className="mt-4 text-sm" style={{ color: '#576574' }}>Target: 19%</div>
          </CardContent>
        </Card>
      </div>

      {/* Sites Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Sites in {district.name}</CardTitle>
            <Button variant="outline" onClick={() => navigate('/site-directory')}>
              View All Sites <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#f5f7fa' }}>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Site ID</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Site Name</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Capacity</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Today's Gen</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>PR</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>CUF</th>
                  <th className="text-left p-4 font-semibold" style={{ color: '#576574' }}>Device Status</th>
                </tr>
              </thead>
              <tbody>
                {districtSites.map((site) => (
                  <tr key={site.id} onClick={() => navigate(`/site/${site.id}`)} className="border-b hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="p-4 font-mono text-sm">{site.id}</td>
                    <td className="p-4 font-medium">{site.name}</td>
                    <td className="p-4">{site.capacity} MW</td>
                    <td className="p-4">{site.todayGen} kWh</td>
                    <td className="p-4">
                      <Badge variant="outline" style={{ backgroundColor: site.pr >= 70 ? '#e8f5f1' : '#fff4e6', color: site.pr >= 70 ? '#10ac84' : '#f39c12' }}>
                        {site.pr}%
                      </Badge>
                    </td>
                    <td className="p-4">{site.cuf}%</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getDeviceStatusColor(site.deviceStatus) }} />
                        <span className="text-sm capitalize">{site.deviceStatus}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Alerts and Complaints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Active Alerts ({districtAlerts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {districtAlerts.length > 0 ? (
              <div className="space-y-3">
                {districtAlerts.map((alert) => (
                  <div key={alert.id} onClick={() => alert.site && navigate(`/site/${alert.site}`)} className={`p-3 rounded-lg border ${alert.site ? 'cursor-pointer hover:shadow-md' : ''}`}>
                    <Badge variant="outline" className="mb-2" style={{ backgroundColor: alert.type === 'critical' ? '#fee' : '#fff4e6', color: alert.type === 'critical' ? '#e74c3c' : '#f97316' }}>
                      {alert.type.toUpperCase()}
                    </Badge>
                    <p className="text-sm font-medium">{alert.message}</p>
                    <p className="text-xs mt-1" style={{ color: '#576574' }}>{alert.timestamp}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#576574' }}>No active alerts for this district</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Open Complaints ({districtComplaints.filter(c => c.status !== 'resolved').length})</CardTitle>
          </CardHeader>
          <CardContent>
            {districtComplaints.filter(c => c.status !== 'resolved').length > 0 ? (
              <div className="space-y-3">
                {districtComplaints.filter(c => c.status !== 'resolved').map((complaint) => (
                  <div key={complaint.id} onClick={() => navigate(`/site/${complaint.site}`)} className="p-3 rounded-lg border cursor-pointer hover:shadow-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm font-semibold">{complaint.id}</span>
                      <Badge variant="outline">{complaint.priority}</Badge>
                    </div>
                    <p className="text-sm font-medium">{complaint.category}</p>
                    <p className="text-xs mt-1" style={{ color: '#576574' }}>{complaint.site}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#576574' }}>No open complaints for this district</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DistrictView;
